import {
  DISPLAY_CURRENCIES,
  ICON_TYPES,
  TIME_RANGES,
} from "@shared/common/constants";
import {
  ApiResponse,
  ClientData,
  ClientStatus,
  FixMeLater,
  GraphDataType,
  Limits,
  PrecisionConfig,
} from "@shared/types";
import { ValueOf } from "@shared/types/utils";
import useMainCoinsAvailableAmounts from "@shared/utils/hooks/useMainCoinsAvailableAmounts";
import { ReactNode } from "react";
import { TranslationKeys } from "@shared/common/i18n/types";

export type TimeRange = (typeof TIME_RANGES)[number];
export type IconType = ValueOf<typeof ICON_TYPES>;
export type DisplayCurrency = keyof typeof DISPLAY_CURRENCIES;
export type DisplayCurrenciesToNumber = {
  [key in DisplayCurrency]?: number;
};
export type UnixTimestamp = number;
export type Selector<T> = (state: FixMeLater) => T;

export interface McpMetadata {
  resource: string;
  authorization_servers: string[];
}

export enum ApiTags {
  SUBGROUP_REVIEWS = "SubGroupReviews",
  WATCHLIST = "Watchlist",
  TRADES = "Trades",
  CLOSED_TRADES = "ClosedTrades",
  USER = "User",
  RECENT_PAYMENTS = "RecentPayments",
  TRADING_VIEW = "TradingView",
  SIGNALS = "Signals",
  CLIENTS = "Clients",
  ADMIN_SUB_GROUP_DATA = "AdminSubGroupData",
  ADMIN_SUB_GROUPS = "AdminSubGroups",
  TRADING_CONFIGURATION = "TradingConfiguration",
  ACCOUNT_CONFIGURATION = "AccountConfiguration",
  BOTS = "Bots",
  MAIN_COINS_AVAILABLE_AMOUNT = "MainCoinsAvailableAmount",
  AFFILIATION_REFERRAL_LINKS = "AffiliationReferralLinks",
  AFFILIATION_PAYOUTS = "AffiliationPayouts",
  GROUPS = "Groups",
  PRICE_CHARTS = "PriceCharts",
  PORTFOLIO_DATA = "PortfolioData",
  GROUP_DATA = "GroupData",
  CHANNEL_CONFIGURATION = "ChannelConfiguration",
  EXTERNAL_ACCOUNT_INVITES = "ExternalAccountInvites",
  INVOICES = "Invoices",
  PAYOUT_METHODS = "PayoutMethods",
  QUOTA_USAGE = "QuotaUsage",
  WHITELABEL_CONFIGURATION = "WhitelabelConfiguration",
  FINANCE = "Finance",
}

export enum SortingOrder {
  DESCEND = -1,
  ASCEND = 1,
}

interface Balance {
  amount?: number;
  quantity?: number;
}

export interface AvailableAmountResponse {
  coin: CoinName;
  limits: Pick<Limits, "amount">;
  data: {
    balance: Balance;
  };
  precision: PrecisionConfig;
}

export interface AvailableQuantityResponse {
  coin: CoinName;
  limits: Pick<Limits, "quantity">;
  amount_conversions: string;
  data: {
    balance: Balance;
  };
  precision: PrecisionConfig;
}

export interface AvailableIconAssetsResponse {
  data: CoinName[];
}

export interface SignalGraphRequest {
  exchange_signal_id: number;
  time_range: string;
  symbol?: string;
  for_admin: boolean;
}

export interface TradeGraphRequest {
  trade_id: number;
  time_range: TimeRange;
  ignore_status?: boolean;
}

export type PriceGraphDataRequest = SignalGraphRequest | TradeGraphRequest;

export interface PriceGraphData {
  graph_data: GraphDataType;
  price_coin: string;
}

export enum AnalyticsPageName {
  BOT_FORM = "Bot Form",
  BOT_SUMMARY = "Bot Summary",
  BOT_EXTRA_DETAILS = "Bot Extra Details",
  TRADE_EXTRA_DETAILS = "Trade Extra Details",
  ACCOUNT_CONFIGURATION = "Account Configuration",
  ACCOUNTS_AND_GROUPS = "Accounts and Groups",
  FILTERS = "Filters",
  AFFILIATION = "Affiliation",
  AFFILIATION_REFERRAL_LINKS = "Affiliation Referral Links",
  AFFILIATION_EXISTING_USERS = "Affiliation Existing Users",
  AFFILIATION_EARNINGS = "Affiliation Earnings",
  AFFILIATION_PAYOUTS = "Affiliation Payouts",
  CREATE_OR_EDIT_REFERRAL_LINK = "Create/Edit Referral Link",
  TRADES = "Trades",
  SIGNALS = "Signals",
  CONNECT_EXTERNAL_ACCOUNT = "Connect External Account",
  BILLING = "Billing",
  TRADING_TERMINAL = "Trading Terminal",
  CREATE_TRADE = "Create Trade",
  BACKTESTING = "Backtesting",
  MARKETPLACE = "Marketplace",
  ACCOUNT_AND_SECURITY = "Account and Security",
  ADMIN = "Admin",
  ASSET_MANAGER = "Asset Manager",
}

export type ExchangeName = string;
export type AccountName = string;
export type CoinName = string;
export type ClientToExchangeMapping = { [key in AccountName]: ExchangeName };
export type ExchangeToClientsMapping = {
  [key in ExchangeName]: AccountName[] | undefined;
};
export type ExchangeToSymbolsMapping = { [key in ExchangeName]: CoinName[] };
export type SymbolToExchangesMapping = { [key in CoinName]: ExchangeName[] };

export interface GetAvailableQuantityOrAmountRequest {
  account_id: number;
  symbol: CoinName;
  reverse: boolean;
}

export interface SymbolMetadata {
  main_coin: string;
  quantity_min?: number;
  quantity_max?: number;
  amount_min?: number;
  amount_max?: number;
  price_min?: number;
  price_max?: number;
  leverage_max?: number;
  contract_size?: number;
  taker_fee?: number;
  is_active: boolean;
  mica_is_active?: boolean;
}

export interface SymbolsMetadata {
  [exchangeName: string]: {
    defaults: SymbolMetadata;
    symbols: {
      [symbol: string]: SymbolMetadata;
    };
  };
}

export interface ExchangeMetadata {
  is_margin_exchange: boolean;
  is_spot_exchange: boolean;
  is_inverse_exchange: boolean;
  limits: {
    price_factor: { min: number; max: number };
    leverage?: number;
  };
  mica_pairs?: string[];
  pairs?: string[];
}

export interface ExchangesMetadata {
  [exchangeName: string]: ExchangeMetadata;
}

export interface MainCoinAmountData {
  amount?: number;
  btc_amount: number;
  usd_amount: number;
}

export type getAccountsMainCoinsAvailableAmountRequest = void | {
  client_id?: number;
  client_ids?: number[];
  return_effective_total?: boolean;
};

export interface getAccountsMainCoinsTotalAndAvailableAmountRequest {
  return_total?: boolean;
  return_client_amounts?: boolean;
  return_effective_total?: boolean;
  client_id?: number;
  client_ids?: number[];
}

export type MainCoinsAvailableAmountData = {
  [accountID: number]: {
    [mainCoinName: string]: MainCoinAmountData;
  };
};

export type MainCoinsAvailableAmountWithEffectiveTotalData = {
  main_coin: {
    available: MainCoinsAvailableAmountData;
    effective_total?: MainCoinsAvailableAmountData;
  };
};

export type MainCoinsAvailableAmountResponseData =
  | MainCoinsAvailableAmountData
  | MainCoinsAvailableAmountWithEffectiveTotalData;

export type MainCoinsTotalAndAvailableAmountData = {
  client: {
    [accountID: number]: Partial<
      Record<GeneralAmountType, { USD: number; BTC: number }>
    >;
  };
  main_coin: Partial<Record<GeneralAmountType, MainCoinsAvailableAmountData>>;
};

export enum GeneralAmountType {
  TOTAL = "total",
  AVAILABLE = "available",
  EFFECTIVE_TOTAL = "effective_total",
}

export type MainCoinsAvailableAmountsData = ReturnType<
  ReturnType<typeof useMainCoinsAvailableAmounts>["getMainCoinsAmountsData"]
>;

export interface MutationStatus<Data = any, Args = any> {
  data?: Data;
  currentData?: Data;
  isUninitialized: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  endpointName?: string | undefined;
  originalArgs?: Args | void;
  error?: { data: ApiResponse<Data>; status: number };
}

export interface LazyQueryStatus<Data = any, Args = any>
  extends MutationStatus<Data, Args> {
  isFetching: boolean;
}

export interface QueryStatus<Data = any, Args = any>
  extends LazyQueryStatus<Data, Args> {
  refetch: () => void;
}

export enum Direction {
  LONG = "Long",
  SHORT = "Short",
}

export enum StopType {
  LIMIT = 1,
  MARKET = 2,
}

export enum EnvType {
  DEMO = 1, // None is normal env
}

export enum EnvironmentType {
  PROD = "prod",
  STAGING = "staging",
  TEST = "test",
  DEV = "dev",
  LOCAL = "local",
}

export enum RequestSource {
  WEBSITE = "website",
  APP = "app",
}

export enum Platform {
  TELEGRAM_BOT = 1,
  MOBILE_APP = 2,
  WEBSITE = 3,
}

export enum PlatformType {
  TELEGRAM = 1,
  DISCORD = 2,
  WEBSITE = 3,
  MOBILE = 4,
  WHOP = 5,
  CORNIX = 6,
}

export enum SubGroupType {
  FREE = 1,
  PREMIUM = 2,
  PERSONAL = 3,
}

export type OpenMessage = (
  text: string,
  type: OpenMessageType,
  duration?: number
) => void;

export type OpenMessageType = "error" | "success" | "warning" | "info";

export type DisplayResultMessage = (
  promise: Promise<any>,
  successMessage?: string,
  defaultError?: string,
  duration?: number,
  onSuccessCallback?: (response: any) => void,
  ignoreStatuses?: number[],
  showSuccess?: boolean,
  useSuccessMessage?: boolean
) => Promise<any>;

export type OnlyHelperTooltipProps = {
  analyticsFieldName?: string;
  analyticsPageName?: string;
  description?: ReactNode;
  articleLink?: string;
  articleId?: string;
};

export type HelperTooltipWithTranslations<Language extends LanguageNameSpaces> =
  OnlyHelperTooltipProps & {
    title: TranslationKeys<Language>;
    description: TranslationKeys<Language>;
  };

export interface StoreNpsResultRequest {
  rating?: number;
  text?: string;
  platform_type: PlatformType;
}

export type OpenConfirmModal = (
  confirmStr: ReactNode | string[],
  handleOk: () => Promise<void> | void,
  isDanger?: boolean | undefined,
  hideCancel?: boolean,
  okText?: string,
  cancelText?: string,
  handleCancel?: () => Promise<void> | void
) => void;

export enum TimeUnit {
  YEAR = "Year",
  DAY = "Day",
  HOUR = "Hour",
  MINUTE = "Minute",
  SECOND = "Second",
}

export enum WorkspaceType {
  ADMIN = "Admin",
  USER = "User",
  DEMO = "Demo",
}

export enum PrecisionType {
  QUANTITY = "quantity",
  AMOUNT = "amount",
  PRICE = "price",
  PERCENT = "percent",
  BALANCE = "balance",
  CHANGE = "change",
  USD_AMOUNT = "usd_amount",
  BTC_AMOUNT = "btc_amount",
  USD_PRICE = "usd_price",
  BTC_PRICE = "btc_price",
  USD_BALANCE = "usd_balance",
  BTC_BALANCE = "btc_balance",
}

export enum UtmParams {
  UTM_ID = "utm_id",
  UTM_SOURCE = "utm_source",
  UTM_MEDIUM = "utm_medium",
  GCLID = "gclid",
}

export interface GetMinimumTimestampRequest {
  exchange: string;
  symbol?: string;
  include_site_db?: boolean;
}

export interface Inset {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export type DisplayCurrencyValue = { USD: number; BTC: number };

export interface SymbolsMetadataManager {
  allSymbols: string[];
  symbolsMetadata: SymbolsMetadata | undefined;
  exchangeToSymbols: Record<string, Set<string>> | undefined;
  getSymbolExchanges: (symbol?: string) => string[];
  getExchangeToSymbolsStatus: QueryStatus<SymbolsMetadata>;
  isSymbolActiveInAccount: (
    symbol?: string,
    accountId?: number
  ) => boolean | undefined;
  isSymbolActiveInExchange: (
    symbol?: string,
    exchange?: string
  ) => boolean | undefined;
  getSymbolMetadata: (
    symbol?: string,
    exchange?: string
  ) => SymbolMetadata | undefined;
  getMainCoin: (symbol?: string, exchange?: string) => string | undefined;
  getDisplayCoin: (symbol?: string, exchange?: string) => string | undefined;
  getAmountConversionFormula: (
    symbol?: string,
    exchange?: string
  ) => string | undefined;
  getQuantityConversionFormula: (
    symbol?: string,
    exchange?: string
  ) => string | undefined;
  getQuantityCoin: (
    symbol?: string,
    exchange?: string,
    isLeftSideOnly?: boolean
  ) => string | undefined;
  getExchangeSymbols: (exchange?: string | undefined) => string[];
  getAccountActiveSymbols: (
    clientId?: number | undefined
  ) => string[] | undefined;
  isSuccess: boolean;
}

export interface ClientsMetadataManager {
  getClientsListStatus: QueryStatus<ClientData[]>;
  getAccountByName: (accountName: string) => ClientData | undefined;
  getAccountById: (accountId?: number) => ClientData | undefined;
  clientExchanges: string[];
  clientIds: number[];
  getAccountExchangeById: (accountId: number) => string | undefined;
  clientsData: ClientData[] | undefined;
  activeClientIds: number[];
  activeClientsData: ClientData[] | undefined;
  writableActiveClientsData: ClientData[] | undefined;
  notDeletedClientsData: ClientData[] | undefined;
  getIsAccountInvalid: (accountId?: number) => boolean | undefined;
  getIsAccountInactive: (accountId?: number) => boolean | undefined;
  getAccountStatus: (accountId?: number) => ClientStatus | undefined;
  getAccountHasActiveDuplicate: (accountId?: number) => boolean;
  getIsMicaAccount: (accountId?: number) => boolean;
  getActiveAccountsWithSameApis: (
    accountId?: number
  ) => ClientData[] | undefined;
  getAccountIsReadOnly: (accountId?: number) => boolean;
  isSuccess?: boolean;
}

export type ElementSize = Omit<DOMRectReadOnly, "toJSON">;

export enum LanguageNameSpaces {
  COMMON = "common",
  AUTH = "auth",
  DASHBOARD = "dashboard",
  TRADES = "trades",
  AFFILIATION = "affiliation",
  PORTFOLIO = "portfolio",
  SETTINGS = "settings",
  GROUPS = "groups",
  WATCHLIST = "watchlist",
  MARKETPLACE = "marketplace",
  BILLING = "billing",
  TRADING_TERMINAL = "tradingTerminal",
  BOTS = "bots",
  SIGNALS = "signals",
  ADMIN = "admin",
  ONBOARDING = "onboarding",
  PAYOUTS = "payouts",
  ASSET_MANAGER = "assetManager",
  USERS_MANAGEMENT = "usersManagement",
}

export enum ExchangeTag {
  FUTURES = "Futures",
  SPOT = "Spot",
  INVERSE = "Inverse",
}
